import { useState, useRef, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import AppFooter from '../components/shared/AppFooter'

const NAV_ITEMS = [
  { label: 'Our approach to safety', key: 'approach' },
  { label: 'How Omore works', key: 'how-it-works' },
  { label: 'Information we collect', key: 'data' },
  { label: 'Parent consent and controls', key: 'consent' },
  { label: 'FAQs', key: 'faqs' },
] as const

export default function SafetyPage() {
  const [searchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const initialIndex = NAV_ITEMS.findIndex((n) => n.key === tabParam)

  const [active, setActive] = useState(initialIndex >= 0 ? initialIndex : 0)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (initialIndex >= 0) {
      setActive(initialIndex)
    }
  }, [initialIndex])

  const handleNav = (index: number) => {
    setActive(index)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="relative min-h-screen bg-base text-ink flex flex-col">
      <div className="mx-auto max-w-6xl px-4 pt-6 w-full">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-ink/60 hover:text-ink">
          ← Back
        </Link>
      </div>

      <div className="mx-auto flex max-w-6xl flex-col lg:flex-row">
        {/* Left sidebar */}
        <aside className="shrink-0 lg:sticky lg:top-16 lg:w-64 lg:self-start">
          <nav className="flex gap-1 overflow-x-auto px-4 py-4 lg:flex-col lg:gap-0 lg:overflow-x-visible lg:px-6 lg:py-12">
            {NAV_ITEMS.map((item, i) => (
              <button
                key={item.key}
                onClick={() => handleNav(i)}
                className={`group flex items-center gap-3 whitespace-nowrap rounded-lg px-3 py-2.5 text-left text-sm transition-all lg:whitespace-normal lg:rounded-none lg:px-0 lg:py-3 ${
                  active === i
                    ? 'font-bold text-ink'
                    : 'font-medium text-ink/40 hover:text-ink/75'
                }`}
              >
                <span
                  className={`hidden h-2.5 w-2.5 shrink-0 rounded-full border-2 transition-all lg:inline-block ${
                    active === i
                      ? 'border-amber bg-amber'
                      : 'border-ink/30 bg-transparent group-hover:border-ink/50'
                  }`}
                />
                {item.label}
              </button>
            ))}

            <div className="mt-4 px-3 pb-2 lg:mt-8 lg:px-0">
              <div className="rounded-xl border border-amber/20 bg-amber/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-amber">
                  Safety concerns?
                </p>
                <p className="mt-1.5 text-xs leading-relaxed text-ink/60">
                  Reach us anytime at{' '}
                  <a
                    href="mailto:safety@omore.app"
                    className="font-semibold text-ink underline underline-offset-2 hover:text-amber"
                  >
                    safety@omore.app
                  </a>
                </p>
              </div>
            </div>
          </nav>
        </aside>

        {/* Right column */}
        <main
          ref={contentRef}
          className="min-w-0 flex-1 px-4 py-8 lg:px-12 lg:py-12"
        >
          {active === 0 && <SectionApproach />}
          {active === 1 && <SectionHowItWorks />}
          {active === 2 && <SectionDataCollection />}
          {active === 3 && <SectionParentConsent />}
          {active === 4 && <SectionFAQ />}
        </main>
      </div>
      <AppFooter />
    </div>
  )
}

function SectionApproach() {
  return (
    <section className="max-w-2xl space-y-8">
      <div className="space-y-4">
        <h1 className="font-display text-3xl font-bold text-ink">
          Our approach to safety
        </h1>
        <p className="text-lg font-light leading-relaxed text-amber">
          The internet can be noisy and ruthless for young people. We built a quiet corner where that’s not allowed to happen.
        </p>
        <p className="text-lg font-light leading-relaxed text-ink/70">
          Omore is a free website built for under-16s to send beautiful visual messages called ‘Postys’ to hype up their friends, family and teachers. Think postcards for the internet. A corner of the internet where people are nice to each other. Imagine that!
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {[
          {
            title: 'No DMs or chats',
            desc: "Postys are one-way. They can't start conversations.",
          },
          {
            title: 'No photos or profiles',
            desc: 'No uploads, no profile pictures, no follower counts.',
          },
          {
            title: 'No like buttons',
            desc: 'Nothing to tally. Nothing to compare.',
          },
          {
            title: 'Pre-written messages only',
            desc: 'Every posty uses a pre-defined positive message. Always.',
          },
        ].map((card) => (
          <div
            key={card.title}
            className="rounded-xl border border-white/10 bg-surface p-5"
          >
            <h3 className="font-semibold text-ink">{card.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-ink/70">
              {card.desc}
            </p>
          </div>
        ))}
      </div>
    </section>
  )
}

function SectionHowItWorks() {
  return (
    <section className="max-w-2xl space-y-8">
      <div className="space-y-4">
        <h1 className="font-display text-3xl font-bold text-ink">
          How Omore works
        </h1>
        <p className="text-lg font-light text-amber">
          Designed for safety at every step.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {[
          {
            title: 'Postys are shared as links only',
            desc: 'Shared links expire automatically after 7 days. Postys can be downloaded as keepsakes.',
          },
          {
            title: 'Controlled name inputs',
            desc: 'Name fields accept letters only, with built-in filters for inappropriate words.',
          },
          {
            title: 'No account required to start',
            desc: 'Users can send Postys without signing up. No data collected until they choose to.',
          },
          {
            title: 'Under-13 requires parental consent',
            desc: 'Users under 13 need a parent to approve account creation before they get in.',
          },
        ].map((card) => (
          <div
            key={card.title}
            className="rounded-xl border border-amber/15 bg-amber/5 p-5"
          >
            <h3 className="font-semibold text-amber">{card.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-amber/80">
              {card.desc}
            </p>
          </div>
        ))}
      </div>
    </section>
  )
}

function SectionDataCollection() {
  return (
    <section className="max-w-2xl space-y-8">
      <div className="space-y-4">
        <h1 className="font-display text-3xl font-bold text-ink">
          Information we collect
        </h1>
        <p className="text-lg font-light text-amber">
          Very little. On purpose.{' '}
          <span className="ml-2 inline-flex items-center rounded-full border border-amber/30 bg-amber/10 px-3 py-0.5 text-xs font-semibold text-amber">
            COPPA-compliant
          </span>
        </p>
        <p className="text-base font-light leading-relaxed text-ink/70">
          We collect only what’s absolutely necessary to make the product work. Nothing that identifies a user in the real world.
        </p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-surface/50 p-8 backdrop-blur-sm">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-amber">
          What we collect
        </h2>
        <div className="mt-3 overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full min-w-[500px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-white/5">
                <th className="px-4 py-3 font-semibold text-ink">Data</th>
                <th className="px-4 py-3 font-semibold text-ink">Under 13</th>
                <th className="px-4 py-3 font-semibold text-ink">13 and over</th>
              </tr>
            </thead>
            <tbody className="text-ink/70">
              {([
                ['Username', 'User-chosen, login only — never public', 'User-chosen, login only — never public'],
                ['Password', 'Securely hashed', 'Securely hashed'],
                ['Display name (2–15 chars)', 'Required — shown on landscape & classrooms', 'Required — shown on landscape & classrooms'],
                ['Birth month & year', 'Age group determination', 'Age group determination'],
                ['Parent/guardian email', 'Required for consent & parental rights', 'Not collected'],
                ['Email address', 'Not collected', 'Required for login and password reset'],
                ['Parent PIN', 'Set by parent during consent, securely hashed', 'Not applicable'],
              ] as const).map(([data, under13, over13]) => (
                <tr key={data} className="border-b border-white/5">
                  <td className="px-4 py-2.5 font-medium text-ink">{data}</td>
                  <td className="px-4 py-2.5">{under13}</td>
                  <td className="px-4 py-2.5">{over13}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-surface/50 p-8 backdrop-blur-sm">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-amber">
          What we don’t collect
        </h2>
        <p className="mt-3 text-sm text-ink/70">
          Real name &middot; Exact date of birth (only month &amp; year) &middot; Phone number &middot; Physical address &middot; Photo or profile picture &middot; Geolocation &middot; Social media handles
        </p>
      </div>
    </section>
  )
}

const FAQ_ITEMS = [
  {
    q: 'What is Omore?',
    a: 'Omore is a free website for under 16s to send digital postcards instead of messages. Unlike texting or social media, it does not allow free text, chats, or comments. Kids choose from pre-written, moderated messages to send hype, funny, or thoughtful Postys to their friends without pressure or risk.',
  },
  {
    q: 'Is Omore safe for kids under 13?',
    a: 'Yes. Omore is designed specifically as a safe website for kids under 13. It protects children by not allowing free text messaging, not having chats, comments, or DMs, and using only pre-written, moderated messages. The product is safe by design.',
  },
  {
    q: 'What age group is Omore for?',
    a: "Omore is specially designed for under-16s to hype up their squad. Under-13s require verifiable parental consent before creating an account, in line with COPPA requirements. But anyone can use the website to be nice on the platform. :)",
  },
  {
    q: 'What makes Omore different from texting or messaging apps?',
    a: 'Omore is an alternative to texting for kids. Unlike apps like WhatsApp or traditional messaging, there is no typing or free text, there are no conversations or chat threads, and messages are pre-written and moderated. This removes pressure, overthinking, and the risk of harmful communication.',
  },
  {
    q: 'Is Omore a social media app?',
    a: 'No. Omore is not a social media platform. There are no likes, no followers, no comments, no algorithmic feeds. It is a safe alternative to social media for kids, focused on expression and positive interaction.',
  },
  {
    q: 'What is a safe alternative to social media for tweens?',
    a: 'Omore is a safe alternative to social media for tweens under 16. Instead of public profiles, follower counts and constant engagement, it offers simple, positive interactions in a controlled, safe environment.',
  },
  {
    q: 'How is Omore different from WhatsApp or Instagram for kids?',
    a: 'Omore is fundamentally different from WhatsApp, Instagram, or similar social media platforms. It does not include messaging or chats, comments or replies, user-generated text, or social metrics like likes or followers.',
  },
  {
    q: 'Can kids talk to strangers on Omore?',
    a: 'No. Omore does not allow chatting or conversations with anyone. There are no DMs, no messaging threads, no open communication. Postys are one-way, pre-written messages. This makes it a safe, no-chat communication platform for kids.',
  },
  {
    q: "Why can't kids type their own messages?",
    a: "Omore removes typing to make communication safer and easier for kids. By using pre-written messages, kids don't feel pressure to \"say the perfect thing\", harmful or inappropriate language is prevented, and communication stays positive and safe. It's designed to reduce anxiety and encourage them to be nice.",
  },
  {
    q: 'Do you collect personal information from kids?',
    a: 'No. Omore does not collect personal information like real names, emails, or phone numbers. For a detailed list of information we collect, check out the "Information we collect" section on this page.',
  },
  {
    q: 'Can someone send something mean or inappropriate on Omore?',
    a: "It's highly unlikely. Omore prevents harmful communication by design. Kids can only send messages that are already approved and positive.",
  },
  {
    q: 'How are Postys shared?',
    a: 'Postys can only be shared as links. Posty links expire after 7 days. But Postys can be downloaded as image/video for keepsake.',
  },
  {
    q: 'What is an "Atlas" in Omore?',
    a: 'An Atlas is a personal space that grows as kids send Postys. It is a visual representation of their activity and a way to reap the rewards for being nice.',
  },
  {
    q: 'How can teachers use Omore for classrooms?',
    a: "Omore has a dedicated Classroom Mode where teachers and educators can create Posty sessions and share the 6-character join code with the class. Up to 40 students can join the class. It's a low setup and fun activity to do as a group. Within the classroom, students can send Postys to each other and the teacher. They have a shared class Atlas that unlocks with every Posty the class sends.",
  },
  {
    q: "Can a parent delete a child's account?",
    a: "For an under-13 user, the parent has full access to manage their kid's account and can delete their account at any time. Check out the \"Parent consent and controls\" section on this page for more information.",
  },
  {
    q: 'How much does Omore cost?',
    a: "Not a dime! Omore is, and always will be free. There are no subscriptions, purchases or hidden fees.",
  },
]

function SectionFAQ() {
  const [open, setOpen] = useState<number | null>(null)

  return (
    <section className="max-w-2xl space-y-8">
      <div className="space-y-4">
        <h1 className="font-display text-3xl font-bold text-ink">
          Omore – FAQs
        </h1>
        <p className="text-lg font-light text-amber">
          Everything you might want to know, answered.
        </p>
      </div>

      <div className="space-y-3">
        {FAQ_ITEMS.map((item, i) => (
          <div
            key={i}
            className="rounded-xl border border-white/10 bg-surface/50 backdrop-blur-sm"
          >
            <button
              onClick={() => setOpen(open === i ? null : i)}
              className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left"
            >
              <span className="text-sm font-semibold text-ink">{item.q}</span>
              <span
                className={`shrink-0 text-amber transition-transform duration-200 ${open === i ? 'rotate-45' : ''}`}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
            </button>
            {open === i && (
              <p className="px-6 pb-5 text-sm leading-relaxed text-ink/70">
                {item.a}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

function SectionParentConsent() {
  return (
    <section className="max-w-2xl space-y-8">
      <div className="rounded-2xl border border-white/10 bg-surface/50 p-8 backdrop-blur-sm">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-amber">
          How Parent Consent Works
        </h2>
        <ol className="mt-3 space-y-2 text-sm text-ink/70">
          {[
            "Child enters parent's email address when creating an account.",
            'Parent receives a Direct Notice email explaining the platform and data collected.',
            'Parent taps "I approve this account" and sets a 4-digit Parent PIN.',
            'Child completes their account only after parent approves.',
          ].map((step, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber/20 text-xs font-bold text-amber">
                {i + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
        <p className="mt-2 text-xs text-ink/40">
          Consent link expires after 7 days. Max 3 consent emails per 7-day period. If parent declines, no account data is retained.
        </p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-surface/50 p-8 backdrop-blur-sm">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-amber">
          Parent Controls
        </h2>
        <p className="mt-2 text-sm text-ink/70">
          All parent actions available at{' '}
          <a href="https://parent.omore.app" target="_blank" rel="noopener noreferrer" className="font-semibold text-ink underline">
            parent.omore.app
          </a>,
          authenticated with email and 4-digit PIN.
        </p>
        <div className="mt-3 overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full min-w-[400px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-white/5">
                <th className="px-4 py-3 font-semibold text-ink">Action</th>
                <th className="px-4 py-3 font-semibold text-ink">What Happens</th>
              </tr>
            </thead>
            <tbody className="text-ink/70">
              {([
                ['View data', 'See display name, username, posty count, landscape preview, classroom membership'],
                ['Export data', "Download a file of all data linked to child's account"],
                ['Delete account', 'Immediate deactivation; permanent deletion after 30-day grace period'],
                ['Revoke consent', 'Account frozen immediately; data preserved 30 days then permanently deleted'],
                ['Update email', 'Change parent email on file (PIN required)'],
              ] as const).map(([action, desc]) => (
                <tr key={action} className="border-b border-white/5">
                  <td className="px-4 py-2.5 font-medium text-ink">{action}</td>
                  <td className="px-4 py-2.5">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 text-center">
          <a
            href="https://parent.omore.app"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-10 items-center rounded-2xl border border-violet/40 bg-violet/10 px-6 text-sm font-semibold text-violet transition-all hover:border-violet/60 hover:bg-violet/20"
          >
            Go to Parent Dashboard &rarr;
          </a>
        </div>
      </div>
    </section>
  )
}
